// MA-894 — the Badge Book's spoken lines (KID-JOURNEY-2026-10-08 §5, §11;
// docs/build/specs/MA-894-badge-book.md §6).
//
// ONE source for every line the book speaks, so the screen and the narration
// enumerator (scripts/content/narratableStrings.mjs enumerateUiStrings) cannot
// drift. Recorded lines are REUSED, never re-typed: gold, working and later
// taps go through speakTextForStone (the 378-line Journey domain). Only lines
// nothing recorded could say were new with MA-894, and all of them are listed in
// BADGE_BOOK_NEW_SPEAK_STRINGS, which the enumerator reads, so the narration
// coverage gate fails on any of them without a recorded clip (all 82 were recorded
// in MA-894 step B; the temporary carve-out is closed and no longer exists).

import { KID_LABEL_MAP } from "./journeyView";
import { speakTextForStone } from "./journeyStoneSpeak";
import { journeyChapterTitles } from "./journeyChapterSpeak";
import type { Status } from "./skillTreeStatus";
import { PRACTICE_DAY_THRESHOLDS } from "./badges/ids";
import type { BadgeLook, MedalLook } from "./badges/look";

/** The book's header and shelf lines (spoken by their speaker buttons). */
export const BADGE_BOOK_TITLE_TEXT = "My badges";
export const BADGE_BOOK_NEXT_UP_TEXT = "Next up!";
export const BADGE_BOOK_NEXT_UP_HINT_TEXT = "Tap one to practice it.";
export const BADGE_BOOK_DAYS_SHELF_TEXT = "Practice days";
export const BADGE_BOOK_UI_SPEAK_TEXTS: readonly string[] = Object.freeze([
  BADGE_BOOK_TITLE_TEXT,
  BADGE_BOOK_NEXT_UP_TEXT,
  BADGE_BOOK_NEXT_UP_HINT_TEXT,
  BADGE_BOOK_DAYS_SHELF_TEXT,
]);

/** Printed only (never spoken): the tab label and the header with the child's name. */
export const BADGES_TAB_LABEL = "Badges";
export function badgeBookOwnerText(name: string): string {
  return `${name}'s badges`;
}

/** The silver badge's tap line (new). */
export function silverBadgeSpeakText(kidLabel: string): string {
  return `${kidLabel} — Silver badge! Keep practicing to make it gold.`;
}

/** The spoken line of a skill badge, by its look (§6):
 *  gold → the recorded "{label} — Finished.";
 *  silver → the new silver line;
 *  working / later → the recorded "{label} — {status phrase}." over the TREE
 *  status, except a secure/mastered tree status whose stricter kid badge has
 *  not landed, which speaks as "Working on it" (never "Finished"/"Strong" over
 *  a working badge). */
export function speakTextForSkillBadge(look: BadgeLook, kidLabel: string, treeStatus: Status): string {
  if (look === "gold") return speakTextForStone(kidLabel, "mastered");
  if (look === "silver") return silverBadgeSpeakText(kidLabel);
  const status: Status = treeStatus === "secure" || treeStatus === "mastered" ? "practicing" : treeStatus;
  return speakTextForStone(kidLabel, status);
}

export function trophySpeakText(titleKid: string, look: MedalLook): string {
  return `${titleKid} trophy — ${look === "earned" ? "Finished" : "Coming up"}.`;
}

export function dayBadgeName(n: number): string {
  return n === 1 ? "1 practice day" : `${n} practice days`;
}

export function dayBadgeSpeakText(n: number, look: MedalLook): string {
  return `${dayBadgeName(n)} — ${look === "earned" ? "Finished" : "Coming up"}.`;
}

/** Every NEW spoken line of the book, in a stable order (56 silver + 8 trophy
 *  + 14 practice-day + 4 header/shelf = 82 on main 92bf8781). */
export function badgeBookNewSpeakStrings(): string[] {
  const out: string[] = [];
  for (const label of Object.values(KID_LABEL_MAP)) out.push(silverBadgeSpeakText(label));
  for (const title of journeyChapterTitles()) {
    out.push(trophySpeakText(title, "earned"), trophySpeakText(title, "later"));
  }
  for (const n of PRACTICE_DAY_THRESHOLDS) out.push(dayBadgeSpeakText(n, "earned"), dayBadgeSpeakText(n, "later"));
  out.push(...BADGE_BOOK_UI_SPEAK_TEXTS);
  return [...new Set(out)];
}
export const BADGE_BOOK_NEW_SPEAK_STRINGS: readonly string[] = Object.freeze(badgeBookNewSpeakStrings());
